import type { IActivity } from "~/interface/Activity"
import Activity from "~/class/Activity"
import type { JobCard } from "~/interface/Job"

const LOCAL_STORAGE_PREFIX = "activity-dynamodb"

const saveLocalStorage = (activity: IActivity) => {
  if (!process.client) return

  try {
    const key = `${LOCAL_STORAGE_PREFIX}:${activity.Nonce || Date.now()}`
    localStorage.setItem(key, JSON.stringify(activity))
  } catch (error) {
    console.error("Failed to save activity to local storage:", error)
  }
}

const removeLocalStorage = (activity: IActivity) => {
  if (!process.client) return

  try {
    const key = `${LOCAL_STORAGE_PREFIX}:${activity.Nonce}`
    localStorage.removeItem(key)
  } catch (error) {
    console.error("Failed to remove activity from local storage:", error)
  }
}

export const getPendingActivities = (): IActivity[] => {
  if (!process.client) return []

  try {
    const items: IActivity[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(LOCAL_STORAGE_PREFIX)) {
        const stored = localStorage.getItem(key)
        if (stored) {
          items.push(JSON.parse(stored))
        }
      }
    }
    return items
  } catch (error) {
    console.error("Failed to get pending activities from local storage:", error)
    return []
  }
}

const postActivity = async (jobCard: JobCard, dataservice: ReturnType<typeof useDataservice>, customerId?: string, businessDate?: string) => {
  
   
  const activity = new Activity().fromJobCard(jobCard, customerId, businessDate) as IActivity

  //clean undefined in  obj's prop
  Object.keys(activity).forEach((key) => {
    if (activity[key] === undefined) {
      delete activity[key]
    }
  })



  saveLocalStorage(activity)

  try {
    await dataservice.activities.postWithKey(activity.customerId, activity)
    removeLocalStorage(activity)
  } catch (error) {
    console.error("Failed to post activity to API, kept in local storage:", error)
  }
}

export const syncPendingActivities = async (dataservice: ReturnType<typeof useDataservice>) => {
  const pending = getPendingActivities()
  if (!pending.length) return

  const batchSize = 25
  const batches: IActivity[][] = []
  
  for (let i = 0; i < pending.length; i += batchSize) {
    batches.push(pending.slice(i, i + batchSize))
  }
  
  for (const batch of batches) {
    try {
      await Promise.all(
        batch.map(async (activity) => {
          await dataservice.activities.postWithKey(activity.customerId, activity)
          removeLocalStorage(activity)
        })
      )
    } catch (error) {
      console.error("Failed to sync pending activities batch:", error)
    }
  }
}

export default defineNuxtPlugin(({ $pinia }) => {
  if (!process.client) return
    
  const jobV1Store = usePosJobsStore()
  const dataservice = useDataservice()
  const customerId = dataservice.customerId

  onDataserviceReady(dataservice, () => void syncPendingActivities(dataservice))
  window.addEventListener("online", () => {
    if (dataservice.isInited.value) void syncPendingActivities(dataservice)
  })

  jobV1Store.$onAction((action) => {
    if (action.name == "add") {
      const job = action.args[0] as JobCard
      const isSync = action.args[1]
      isSync && action.after(() => {
        
        const businessDate = jobV1Store.getBusinessDate()
        postActivity(job, dataservice, customerId.value, businessDate)
      })
    }
  })
})
